<template>
  <div class="archive-project-single">
    <app-view-layout :header="project.name">
      <template #toolbarMenu>
        <SingleArchiveProjectToolbarMenu
          :project-id="id"
          :actions="actions"
          @print-report="printReport({id, name: project.name})"
        />
      </template>

      <template #content>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <ProjectThumbnailCard
              :image="project.thumbnail"
              :youtube="project.youtubeThumbnailUrl"
            />

            <ProjectDetailsCard
              class="mt-8"
              :project="project"
            />

            <ProjectChangesListCard
              v-if="project.history"
              class="mt-8"
              :history="project.history"
            />

            <ProjectChatCard
              v-if="project.chat"
              class="mt-8"
              :chat="project.chat"
              :project-id="id"
            />
          </v-col>

          <v-col
            cols="12"
            md="8"
          >
            <v-row>
              <v-col cols="12">
                <ProjectStatusCard :project="project" />
              </v-col>
              <v-col
                v-if="project.campaigns"
                cols="12"
                md="6"
              >
                <ProjectCampaignsCard :value="project.campaigns" />
              </v-col>
              <v-col
                v-if="project.tags"
                cols="12"
                md="6"
              >
                <ProjectTagsCard :value="project.tags" />
              </v-col>
            </v-row>

            <v-row>
              <v-col
                v-if="project.youtubeLink"
                cols="12"
              >
                <v-card class="pa-4">
                  <label class="label">Youtube url:</label>
                  <AppCopyUrl
                    input-id="youtubeLink"
                    :url="project.youtubeLink"
                  />
                </v-card>
              </v-col>

              <v-col
                v-if="project.hyperlink || project.discLocation"
                cols="12"
              >
                <ProjectAnchorsCard :project="project" />
              </v-col>

              <v-col v-if="project.members.length">
                <ProjectTeamCard
                  :users="project.members"
                  title="Zespół projektowy"
                  empty-state-text="Nie dodano członków zespołu projektowego"
                />
              </v-col>

              <v-col v-if="project.orderers.length">
                <ProjectTeamCard
                  :users="project.orderers"
                  title="Zespół zlecający"
                  empty-state-text="Nie dodano członków zespołu zlecającego"
                />
              </v-col>

              <v-col
                v-if="project.files"
                cols="12"
                :order="user.layoutType === 'graphic_designer_layout' ? 'first' : undefined"
              >
                <ProjectFilesCard
                  ref="projectFiles"
                  :disable-addition="!filesAdditionPermitted"
                  hide-tabs
                  disable-delete
                  :files="project.files"
                  :processing="isProcessing"
                  @add="showFileAddDialog"
                />
              </v-col>

              <v-col
                v-if="project.finalFiles"
                cols="12"
                :order="user.layoutType === 'graphic_designer_layout' ? 'first' : undefined"
              >
                <ProjectFilesCard
                  ref="projectFinalFiles"
                  title="Pliki finalne"
                  disable-addition
                  hide-tabs
                  disable-delete
                  :files="project.finalFiles"
                  :processing="isProcessing"
                />
              </v-col>

              <v-col
                v-if="project.description !== null"
                cols="12"
              >
                <app-text-card
                  :value="project.description"
                  title="Opis projektu"
                  disabled
                >
                  <AppEmptyState
                    title="Brak opisu projektu"
                    subtitle="Opis projektu nie został dodany"
                    class="pb-16"
                  />
                </app-text-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <app-text-card
              v-if="project.copy"
              :value="project.copy"
              title="Tekst do wykorzystania"
              disabled
            >
              <AppEmptyState
                title="Brak tekstu"
                subtitle="Tekst do wykorzystania nie został dodany."
                class="pb-16"
              />
            </app-text-card>
          </v-col>
        </v-row>
      </template>
    </app-view-layout>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import AppViewLayout from '@/components/Layout/AppViewLayout.vue';
import ProjectThumbnailCard from '@/components/Projects/ProjectThumbnailCard.vue';
import ProjectDetailsCard from '@/components/Projects/ProjectDetailsCard.vue';
import ProjectTeamCard from '@/components/Projects/ProjectTeamCard.vue';
import ProjectChangesListCard from '@/components/Projects/Changes/ProjectChangesListCard.vue';
import ProjectChatCard from '@/components/Projects/ProjectChatCard.vue';
import ProjectFilesCard from '@/components/Projects/ProjectFilesCard.vue';
import ProjectCampaignsCard from '@/components/Projects/ProjectCampaignsCard.vue';
import ProjectAnchorsCard from '@/components/Projects/ProjectAnchorsCard.vue';
import ProjectStatusCard from '@/components/Projects/ProjectStatusCard.vue';
import ProjectTagsCard from '@/components/Projects/ProjectTagsCard.vue';
import AppTextCard from '@/components/UI/AppTextCard.vue';
import AppCopyUrl from '@/components/UI/AppCopyUrl.vue';
import SingleArchiveProjectToolbarMenu from '@/components/Projects/SingleArchiveProjectToolbarMenu.vue';
import AppEmptyState from '@/components/UI/AppEmptyState.vue';

export default {
  components: {
    AppViewLayout,
    SingleArchiveProjectToolbarMenu,
    ProjectThumbnailCard,
    ProjectDetailsCard,
    ProjectTeamCard,
    ProjectChangesListCard,
    ProjectChatCard,
    ProjectFilesCard,
    ProjectCampaignsCard,
    ProjectStatusCard,
    ProjectAnchorsCard,
    ProjectTagsCard,
    AppTextCard,
    AppCopyUrl,
    AppEmptyState,
  },

  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },

  computed: {
    ...mapState('project', [
      'project',
      'isProcessing',
      'actions'
    ]),

    ...mapState('auth', [
      'user',
    ]),

    ...mapGetters('auth', [
      'checkUserPermissions',
    ]),

    filesAdditionPermitted() {
      const { orderers, members } = this.project;
      const isMember = [...orderers, ...members]
        .map((member) => member.userId)
        .includes(this.user.id);

      return this.checkUserPermissions(['graphic']) && isMember;
    },
  },

  created() {
    this.fetchProject(this.id)
      .then(() => {
        this.fetchProjectActions(this.id);
      });
  },

  beforeDestroy() {
    this.resetProject();
  },

  methods: {
    ...mapActions('project', [
      'fetchProject',
      'resetProject',
      'printReport',
      'fetchProjectActions'
    ]),

    ...mapActions('dialog', [
      'showDialog',
    ]),

    showFileAddDialog() {
      this.showDialog({
        header: 'Dodawanie pliku',
        text: 'Wybierz plik i dodaj do projektu.',
        component: 'FileUpload',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  .archive-project-single::v-deep .project-status--clickable{
    cursor: default;
  }
</style>
